import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';

import type { DialogEdge, DialogNode, ScenarioContent } from '../../api/types';
import {
  addEdge,
  layoutGraph,
  NODE_TYPES,
  positionsOf,
  removeNode,
  renameNode,
  uniqueNodeId,
  type GraphIssue,
  type Point,
} from './graph';

export const NODE_W = 140;
export const NODE_H = 48;
const STEP = 10;

type Selection = { kind: 'node'; id: string } | { kind: 'edge'; index: number } | null;

type Drag =
  | { kind: 'move'; id: string; offset: Point; moved: boolean }
  | { kind: 'connect'; from: string; at: Point };

interface Props {
  content: ScenarioContent;
  onChange: (content: ScenarioContent) => void;
  issues: GraphIssue[];
}

function conditionText(condition: unknown): string {
  if (condition === undefined || condition === null) return '';
  return typeof condition === 'string' ? condition : JSON.stringify(condition);
}

function parseCondition(value: string): unknown {
  const v = value.trim();
  if (!v) return undefined;
  if (v.startsWith('{')) {
    try {
      return JSON.parse(v);
    } catch {
      return value;
    }
  }
  return value;
}

/** Точка на границе прямоугольника узла в сторону цели — чтобы стрелка не пряталась под узлом. */
function borderPoint(from: Point, to: Point): Point {
  const cx = from.x + NODE_W / 2;
  const cy = from.y + NODE_H / 2;
  const dx = to.x + NODE_W / 2 - cx;
  const dy = to.y + NODE_H / 2 - cy;
  if (!dx && !dy) return { x: cx, y: cy };
  const scale = Math.min(Math.abs(NODE_W / 2 / (dx || 1e-9)), Math.abs(NODE_H / 2 / (dy || 1e-9)));
  return { x: cx + dx * scale, y: cy + dy * scale };
}

export function GraphEditor({ content, onChange, issues }: Props) {
  const { t } = useTranslation();
  const svgRef = useRef<SVGSVGElement>(null);
  const [selection, setSelection] = useState<Selection>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [newType, setNewType] = useState<string>('say');
  const [announce, setAnnounce] = useState('');

  const positions = positionsOf(content);
  const start = typeof content.start === 'string' && content.start ? content.start : content.nodes[0]?.id;
  const width = Math.max(640, ...[...positions.values()].map((p) => p.x + NODE_W + 40));
  const height = Math.max(240, ...[...positions.values()].map((p) => p.y + NODE_H + 40));
  const problemNodes = new Set(issues.map((i) => i.node).filter(Boolean));

  const selectedNode = selection?.kind === 'node' ? content.nodes.find((n) => n.id === selection.id) : undefined;
  const selectedEdge = selection?.kind === 'edge' ? content.edges[selection.index] : undefined;

  useEffect(() => {
    if (selection?.kind === 'node' && !content.nodes.some((n) => n.id === selection.id)) setSelection(null);
    if (selection?.kind === 'edge' && !content.edges[selection.index]) setSelection(null);
  }, [content, selection]);

  const toSvg = (e: { clientX: number; clientY: number }): Point => {
    const svg = svgRef.current;
    if (!svg) return { x: e.clientX, y: e.clientY };
    const ctm = typeof svg.getScreenCTM === 'function' ? svg.getScreenCTM() : null;
    if (ctm && typeof svg.createSVGPoint === 'function') {
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const r = pt.matrixTransform(ctm.inverse());
      return { x: r.x, y: r.y };
    }
    const rect = svg.getBoundingClientRect();
    const sx = rect.width ? width / rect.width : 1;
    const sy = rect.height ? height / rect.height : 1;
    return { x: (e.clientX - rect.left) * sx, y: (e.clientY - rect.top) * sy };
  };

  const setNodePosition = (id: string, p: Point) =>
    onChange({
      ...content,
      nodes: content.nodes.map((n) =>
        n.id === id ? { ...n, position: { x: Math.max(0, Math.round(p.x)), y: Math.max(0, Math.round(p.y)) } } : n,
      ),
    });

  const updateNode = (id: string, patch: Partial<DialogNode>) =>
    onChange({ ...content, nodes: content.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)) });

  const updateEdge = (index: number, patch: Partial<DialogEdge>) =>
    onChange({ ...content, edges: content.edges.map((e, i) => (i === index ? { ...e, ...patch } : e)) });

  const nodeAt = (p: Point): string | undefined =>
    content.nodes.find((n) => {
      const pos = positions.get(n.id);
      return pos && p.x >= pos.x && p.x <= pos.x + NODE_W && p.y >= pos.y && p.y <= pos.y + NODE_H;
    })?.id;

  const onNodePointerDown = (e: PointerEvent, id: string) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    const p = toSvg(e);
    const pos = positions.get(id) ?? { x: 0, y: 0 };
    setSelection({ kind: 'node', id });
    setDrag({ kind: 'move', id, offset: { x: p.x - pos.x, y: p.y - pos.y }, moved: false });
    svgRef.current?.setPointerCapture?.(e.pointerId);
  };

  const onPortPointerDown = (e: PointerEvent, id: string) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    setDrag({ kind: 'connect', from: id, at: toSvg(e) });
    svgRef.current?.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: PointerEvent) => {
    if (!drag) return;
    const p = toSvg(e);
    if (drag.kind === 'move') {
      setNodePosition(drag.id, { x: p.x - drag.offset.x, y: p.y - drag.offset.y });
      if (!drag.moved) setDrag({ ...drag, moved: true });
    } else {
      setDrag({ ...drag, at: p });
    }
  };

  const onPointerUp = (e: PointerEvent) => {
    if (drag?.kind === 'connect') {
      const target = nodeAt(toSvg(e));
      if (target && target !== drag.from) {
        onChange(addEdge(content, drag.from, target));
        setAnnounce(t('graphEditor.connected', { from: drag.from, to: target }));
      }
    }
    setDrag(null);
  };

  const addNode = () => {
    const id = uniqueNodeId(content, newType);
    const maxY = Math.max(0, ...[...positions.values()].map((p) => p.y + NODE_H));
    const node: DialogNode = { id, type: newType, position: { x: 20, y: maxY + 30 } };
    if (newType === 'say' || newType === 'command') node.text = '';
    onChange({ ...content, nodes: [...content.nodes, node] });
    setSelection({ kind: 'node', id });
    setAnnounce(t('graphEditor.nodeAdded', { id }));
  };

  const autoLayout = () => {
    const stripped = { ...content, nodes: content.nodes.map(({ position: _p, ...rest }) => rest as DialogNode) };
    const layout = new Map(layoutGraph(stripped).map((l) => [l.node.id, { x: l.x, y: l.y }]));
    onChange({ ...content, nodes: content.nodes.map((n) => ({ ...n, position: layout.get(n.id) })) });
  };

  const deleteSelection = () => {
    if (selection?.kind === 'node') {
      onChange(removeNode(content, selection.id));
      setAnnounce(t('graphEditor.nodeDeleted', { id: selection.id }));
    } else if (selection?.kind === 'edge') {
      onChange({ ...content, edges: content.edges.filter((_, i) => i !== selection.index) });
    }
    setSelection(null);
  };

  const onNodeKeyDown = (e: KeyboardEvent, id: string) => {
    const pos = positions.get(id) ?? { x: 0, y: 0 };
    const moves: Record<string, Point> = {
      ArrowLeft: { x: -STEP, y: 0 },
      ArrowRight: { x: STEP, y: 0 },
      ArrowUp: { x: 0, y: -STEP },
      ArrowDown: { x: 0, y: STEP },
    };
    if (moves[e.key]) {
      e.preventDefault();
      setSelection({ kind: 'node', id });
      setNodePosition(id, { x: pos.x + moves[e.key].x, y: pos.y + moves[e.key].y });
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setSelection({ kind: 'node', id });
    } else if (e.key === 'Delete') {
      e.preventDefault();
      onChange(removeNode(content, id));
      setSelection(null);
      setAnnounce(t('graphEditor.nodeDeleted', { id }));
    }
  };

  const nodeLabel = (n: DialogNode) =>
    [
      t('graphEditor.nodeLabel', { id: n.id, type: t(`scenarios.nodeTypes.${n.type}`, { defaultValue: n.type }) }),
      n.id === start ? t('graphEditor.start') : '',
      problemNodes.has(n.id) ? t('graphEditor.hasProblems') : '',
    ]
      .filter(Boolean)
      .join(', ');

  const dragLine =
    drag?.kind === 'connect' && positions.get(drag.from)
      ? { from: positions.get(drag.from) as Point, to: drag.at }
      : null;

  return (
    <div className="graph-editor">
      <div className="actions" role="toolbar" aria-label={t('graphEditor.toolbar')}>
        <label>
          {t('graphEditor.newNodeType')}
          <select value={newType} onChange={(e) => setNewType(e.target.value)}>
            {NODE_TYPES.map((tp) => (
              <option key={tp} value={tp}>
                {t(`scenarios.nodeTypes.${tp}`)}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="secondary" onClick={addNode}>
          {t('scenarios.addNode')}
        </button>
        <button type="button" className="secondary" onClick={autoLayout} disabled={!content.nodes.length}>
          {t('graphEditor.autoLayout')}
        </button>
        <button type="button" className="danger" onClick={deleteSelection} disabled={!selection}>
          {t('graphEditor.deleteSelected')}
        </button>
      </div>
      <p className="muted" id="graph-editor-help">
        {t('graphEditor.help')}
      </p>
      <p className="sr-only" role="status" aria-live="polite">
        {announce}
      </p>
      <div className="graph-layout">
        <div className="graph-canvas">
          <svg
            ref={svgRef}
            role="group"
            aria-label={t('scenarios.graph')}
            aria-describedby="graph-editor-help"
            viewBox={`0 0 ${width} ${height}`}
            width={width}
            height={height}
            className="graph"
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={() => setDrag(null)}
            onPointerDown={() => setSelection(null)}
          >
            <defs>
              <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M0 0L10 5L0 10z" fill="currentColor" />
              </marker>
            </defs>
            {content.edges.map((e, i) => {
              const a = positions.get(e.from);
              const b = positions.get(e.to);
              if (!a || !b) return null;
              const p1 = borderPoint(a, b);
              const p2 = borderPoint(b, a);
              const selected = selection?.kind === 'edge' && selection.index === i;
              const label = conditionText(e.condition);
              const edgeName = t('graphEditor.edgeLabel', { from: e.from, to: e.to });
              return (
                <g
                  key={i}
                  className={`edge${selected ? ' selected' : ''}`}
                  role="button"
                  tabIndex={0}
                  aria-label={label ? `${edgeName}: ${label}` : edgeName}
                  aria-pressed={selected}
                  onPointerDown={(ev) => {
                    ev.stopPropagation();
                    setSelection({ kind: 'edge', index: i });
                  }}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter' || ev.key === ' ') {
                      ev.preventDefault();
                      setSelection({ kind: 'edge', index: i });
                    } else if (ev.key === 'Delete') {
                      ev.preventDefault();
                      onChange({ ...content, edges: content.edges.filter((_, j) => j !== i) });
                      setSelection(null);
                    }
                  }}
                >
                  <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} className="edge-hit" />
                  <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} className="edge-line" markerEnd="url(#arrow)" />
                  {label && (
                    <text x={(p1.x + p2.x) / 2} y={(p1.y + p2.y) / 2 - 4} textAnchor="middle" fontSize={10}>
                      {label.length > 24 ? `${label.slice(0, 23)}…` : label}
                    </text>
                  )}
                </g>
              );
            })}
            {content.nodes.map((n) => {
              const p = positions.get(n.id);
              if (!p) return null;
              const selected = selection?.kind === 'node' && selection.id === n.id;
              const text = String(n.text ?? '');
              return (
                <g
                  key={n.id}
                  className={`gnode node-${n.type}${selected ? ' selected' : ''}${problemNodes.has(n.id) ? ' invalid' : ''}`}
                  transform={`translate(${p.x} ${p.y})`}
                  role="button"
                  tabIndex={0}
                  aria-label={nodeLabel(n)}
                  aria-pressed={selected}
                  data-node-id={n.id}
                  onPointerDown={(ev) => onNodePointerDown(ev, n.id)}
                  onKeyDown={(ev) => onNodeKeyDown(ev, n.id)}
                >
                  <rect width={NODE_W} height={NODE_H} rx={8} className="node" />
                  {n.id === start && <circle cx={0} cy={NODE_H / 2} r={5} className="start-mark" />}
                  <text x={NODE_W / 2} y={18} textAnchor="middle" fontSize={11} fontWeight={600}>
                    {n.id.length > 18 ? `${n.id.slice(0, 17)}…` : n.id}
                  </text>
                  <text x={NODE_W / 2} y={34} textAnchor="middle" fontSize={10} className="node-sub">
                    {t(`scenarios.nodeTypes.${n.type}`, { defaultValue: n.type })}
                    {text ? `: ${text.length > 12 ? `${text.slice(0, 11)}…` : text}` : ''}
                  </text>
                  {n.type !== 'end' && (
                    <circle
                      cx={NODE_W}
                      cy={NODE_H / 2}
                      r={7}
                      className="port"
                      aria-hidden="true"
                      onPointerDown={(ev) => onPortPointerDown(ev, n.id)}
                    />
                  )}
                </g>
              );
            })}
            {dragLine && (
              <line
                x1={dragLine.from.x + NODE_W}
                y1={dragLine.from.y + NODE_H / 2}
                x2={dragLine.to.x}
                y2={dragLine.to.y}
                className="edge-line drafting"
                markerEnd="url(#arrow)"
              />
            )}
          </svg>
        </div>
        <aside className="graph-panel" aria-label={t('graphEditor.properties')}>
          {selectedNode ? (
            <NodeProperties
              key={selectedNode.id}
              node={selectedNode}
              content={content}
              isStart={selectedNode.id === start}
              onUpdate={(patch) => updateNode(selectedNode.id, patch)}
              onRename={(to) => {
                onChange(renameNode(content, selectedNode.id, to));
                setSelection({ kind: 'node', id: to });
              }}
              onMakeStart={() => onChange({ ...content, start: selectedNode.id })}
              onConnect={(to) => {
                onChange(addEdge(content, selectedNode.id, to));
                setAnnounce(t('graphEditor.connected', { from: selectedNode.id, to }));
              }}
            />
          ) : selectedEdge && selection?.kind === 'edge' ? (
            <div className="form">
              <h3>{t('graphEditor.edgeProperties')}</h3>
              {(['from', 'to'] as const).map((field) => (
                <label key={field}>
                  {t(`scenarios.${field}`)}
                  <select value={selectedEdge[field]} onChange={(ev) => updateEdge(selection.index, { [field]: ev.target.value })}>
                    {content.nodes.map((n) => (
                      <option key={n.id} value={n.id}>
                        {n.id}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
              <label>
                {t('scenarios.condition')}
                <input
                  value={conditionText(selectedEdge.condition)}
                  onChange={(ev) => updateEdge(selection.index, { condition: parseCondition(ev.target.value) as string | undefined })}
                />
              </label>
              <p className="muted">{t('graphEditor.conditionHint')}</p>
            </div>
          ) : (
            <p className="muted">{t('graphEditor.nothingSelected')}</p>
          )}
        </aside>
      </div>
    </div>
  );
}

function NodeProperties({
  node,
  content,
  isStart,
  onUpdate,
  onRename,
  onMakeStart,
  onConnect,
}: {
  node: DialogNode;
  content: ScenarioContent;
  isStart: boolean;
  onUpdate: (patch: Partial<DialogNode>) => void;
  onRename: (id: string) => void;
  onMakeStart: () => void;
  onConnect: (to: string) => void;
}) {
  const { t } = useTranslation();
  const [id, setId] = useState(node.id);
  const [idError, setIdError] = useState<string | null>(null);
  const targets = content.nodes.filter((n) => n.id !== node.id);
  const [target, setTarget] = useState(targets[0]?.id ?? '');
  const command = (node.command ?? null) as { name?: string; args?: Record<string, unknown> } | null;

  const commitId = () => {
    const next = id.trim();
    if (next === node.id) return setIdError(null);
    if (!next) return setIdError(t('graphEditor.idRequired'));
    if (content.nodes.some((n) => n.id === next)) return setIdError(t('graphEditor.idTaken'));
    setIdError(null);
    onRename(next);
  };

  return (
    <div className="form">
      <h3>{t('graphEditor.nodeProperties')}</h3>
      <label>
        {t('scenarios.nodeId')}
        <input
          value={id}
          aria-invalid={idError ? true : undefined}
          aria-describedby={idError ? 'node-id-error' : undefined}
          onChange={(e) => setId(e.target.value)}
          onBlur={commitId}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitId();
            }
          }}
        />
      </label>
      {idError && (
        <span id="node-id-error" role="alert" className="alert alert-error">
          {idError}
        </span>
      )}
      <label>
        {t('scenarios.nodeType')}
        <select value={node.type} onChange={(e) => onUpdate({ type: e.target.value })}>
          {NODE_TYPES.map((tp) => (
            <option key={tp} value={tp}>
              {t(`scenarios.nodeTypes.${tp}`)}
            </option>
          ))}
        </select>
      </label>
      {node.type !== 'end' && (
        <label>
          {t('scenarios.nodeText')}
          <textarea rows={3} value={String(node.text ?? '')} onChange={(e) => onUpdate({ text: e.target.value })} />
        </label>
      )}
      {node.type === 'listen' && (
        <label>
          {t('graphEditor.expectedText')}
          <input
            value={String(node.expected_text ?? '')}
            onChange={(e) => onUpdate({ expected_text: e.target.value || undefined })}
          />
        </label>
      )}
      {node.type === 'command' && (
        <label>
          {t('graphEditor.commandName')}
          <input
            value={command?.name ?? ''}
            onChange={(e) =>
              onUpdate({ command: e.target.value ? { ...(command ?? {}), name: e.target.value, args: command?.args ?? {} } : undefined })
            }
          />
        </label>
      )}
      <label className="checkbox">
        <input type="checkbox" checked={isStart} disabled={isStart} onChange={onMakeStart} />
        {t('graphEditor.startNode')}
      </label>
      {node.type !== 'end' && targets.length > 0 && (
        <div className="inline-form">
          <label>
            {t('graphEditor.connectTo')}
            <select value={target} onChange={(e) => setTarget(e.target.value)}>
              {targets.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.id}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="secondary" disabled={!target} onClick={() => onConnect(target)}>
            {t('scenarios.addEdge')}
          </button>
        </div>
      )}
    </div>
  );
}
